import { Venue, VenuePinType } from "common/types";
import { cn } from "@/lib/utils";
import { BusinessTypeGlyph } from "@/components/Map/BusinessTypeGlyph";
import GreenPin from "@/assets/pins/green_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import GreyPin from "@/assets/pins/grey_pin.svg";

// Sized to match Google's own (small) POI markers.
export const SIZE_DEFAULT = 26;
export const SIZE_ACTIVE = 34;
export const SIZE_SELECTED = 32;

// orange_pin_blank.svg viewBox dimensions.
const PIN_W = 42;
const PIN_H = 38;
export const PIN_ASPECT = PIN_H / PIN_W;
// White inner-circle centre within the frame (glyph position).
const CIRCLE_CX = 18.5;
const CIRCLE_CY = 16.5;
// Frame is left-of-centre (S badge overflows right); shift so the tail tip
// lands on the marker's bottom-centre anchor.
const FRAME_SHIFT_X = PIN_W / 2 - CIRCLE_CX;

const ORANGE = "#E66000";
const GREY = "#9AA0A6";

export const NAME_TEXT_COLOR = "#3c4043";
export const CATEGORY_TEXT_COLOR = "#70757a";

// White halo so label text stays legible over any map tile (Google POI look).
export const NAME_HALO =
  "0 1px 2px #fff, 1px 0 2px #fff, -1px 0 2px #fff, 0 -1px 2px #fff";

/** Vertical centre of the pin's white circle, for aligning a side label.
 * Shared with BasicMapView's separate label markers for the same anchor. */
export function getPinCentreTop(size: number): number {
  return size * PIN_ASPECT * (CIRCLE_CY / PIN_H);
}

// Mapper-view pins keep their existing solid SVGs.
function getMapperPinSvg(pinType?: VenuePinType): string {
  switch (pinType) {
    case VenuePinType.Green:
      return GreenPin;
    case VenuePinType.Black:
      return BlackPin;
    case VenuePinType.Blue:
      return BluePin;
    case VenuePinType.Purple:
      return PurplePin;
    case VenuePinType.Orange:
      return OrangePin;
    case VenuePinType.Grey:
    default:
      return GreyPin;
  }
}

/** Recolorable pin frame: round head + tail, white inner circle for the
 * glyph, and an "S" badge. `color` = orange (has data) or grey (no data). */
function SociabilityPin({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size * PIN_ASPECT}
      viewBox="0 0 42 38"
      fill="none"
      style={{
        display: "block",
        transform: `translateX(${(FRAME_SHIFT_X / PIN_W) * size}px)`,
      }}
      aria-hidden="true"
    >
      <circle cx="18.5" cy="16.5" r="16.5" fill={color} />
      <path
        d="M19.7887 36.9141C19.0441 37.5415 17.9559 37.5415 17.2113 36.9141L6.66718 28.0294C5.24085 26.8276 6.09074 24.5 7.95591 24.5L29.0441 24.5C30.9093 24.5 31.7592 26.8276 30.3328 28.0294L19.7887 36.9141Z"
        fill={color}
      />
      <circle cx="18.5" cy="16.5" r="14.5" fill="#FAFAFA" />
      {/* "S" badge (top-right) */}
      <path
        d="M33.637 18.7852C32.8579 18.7852 32.2805 18.2432 32.0518 17.2997C31.8668 16.5349 31.7896 16.3023 31.7884 16.3C31.1068 14.1477 30.073 12.9607 29.1614 11.9129C28.1363 10.7344 27.2507 9.71757 27.2172 7.75602C27.1867 5.96461 27.8401 4.31528 29.0571 3.11223C30.2638 1.91948 31.9383 1.24577 33.7724 1.21598C33.8133 1.21541 33.8531 1.21484 33.894 1.21484C37.5974 1.21484 40.5529 4.00019 40.7678 7.69415C40.8657 9.37728 40.516 11.0621 39.7277 12.7011C39.0368 14.1379 37.9858 15.585 36.604 17.0024C35.4509 18.1848 34.4523 18.7852 33.637 18.7852Z"
        fill={color}
      />
      <path
        d="M33.894 1.42966C37.4978 1.42966 40.345 4.15258 40.5525 7.70675C40.7559 11.1985 38.9983 14.2388 36.4491 16.8534C35.2194 18.1143 34.3014 18.5703 33.6376 18.5703C32.8585 18.5703 32.4298 17.9413 32.2621 17.2493C32.0702 16.4581 31.9936 16.2318 31.9936 16.2318C30.5374 11.6339 27.4943 11.3509 27.4332 7.752C27.3722 4.15372 30.1168 1.49096 33.7759 1.43081C33.8157 1.43024 33.8543 1.42966 33.894 1.42966ZM33.894 1C33.8525 1 33.811 1 33.769 1.00115C31.8783 1.03208 30.1508 1.72813 28.9044 2.95983C27.6453 4.20471 26.9694 5.90904 27.0011 7.75945C27.0356 9.79892 27.9887 10.8937 28.9983 12.0532C29.8967 13.085 30.9143 14.2542 31.5816 16.3607L31.5839 16.3687C31.5839 16.3687 31.6605 16.6013 31.8415 17.3495C32.0916 18.383 32.7629 19 33.637 19C34.5112 19 35.564 18.3784 36.7591 17.1525C38.157 15.7185 39.2214 14.2525 39.9226 12.7945C40.7271 11.1223 41.0838 9.40191 40.9835 7.68211C40.8769 5.84946 40.0995 4.15945 38.7949 2.92432C37.4846 1.68345 35.7438 1 33.894 1Z"
        fill="#FAFAFA"
      />
      <path
        d="M32.1855 9.74334C32.6153 10.2412 33.3881 10.6829 34.1602 10.6582C34.7526 10.6393 35.1156 10.3414 35.1018 9.92093C35.0851 9.40992 34.5693 9.2455 33.8859 9.01177C32.7012 8.62851 31.3079 8.20629 31.2595 6.70305C31.2151 5.335 32.3163 4.41151 34.0248 4.35709C35.251 4.31756 36.3274 4.75009 36.9313 5.51317C36.9808 5.56816 36.9756 5.63576 36.9365 5.67873L36.0289 6.61081C36.0191 6.62112 35.9926 6.64117 35.954 6.64232C35.916 6.64347 35.8872 6.62513 35.8774 6.61539C35.3789 6.13474 34.7923 5.91247 34.2 5.93194C33.6214 5.95028 33.3397 6.20005 33.3512 6.54607C33.3621 6.8921 33.6704 7.03245 34.4524 7.32348C35.4994 7.71132 37.1814 8.1089 37.2361 9.79261C37.2839 11.2661 36.0721 12.1775 34.3498 12.2331C32.9582 12.2778 31.7959 11.7433 31.1062 10.892C31.0901 10.8731 31.0688 10.8519 31.0676 10.8118C31.0664 10.7717 31.0832 10.7447 31.101 10.7264L32.0351 9.74849C32.0604 9.72501 32.0829 9.71756 32.11 9.71641C32.1371 9.71527 32.1688 9.72271 32.1866 9.74334H32.1855Z"
        fill="#FAFAFA"
      />
      <path
        d="M33.8936 0.75C35.8054 0.75 37.6087 1.45712 38.9668 2.74316C40.3186 4.02309 41.1232 5.77341 41.2334 7.66797C41.3362 9.4332 40.9688 11.196 40.1475 12.9033C39.432 14.3905 38.3508 15.8785 36.9385 17.3271C35.7357 18.5609 34.6192 19.25 33.6367 19.25C32.6164 19.2499 31.8678 18.5207 31.5986 17.4082C31.5087 17.0361 31.4443 16.7932 31.4033 16.6445C31.3829 16.5705 31.3688 16.5198 31.3594 16.4883C31.3547 16.4726 31.3507 16.4618 31.3486 16.4551C31.3476 16.4519 31.3471 16.4496 31.3467 16.4482V16.4473L31.3438 16.4375L31.3428 16.4355C30.6918 14.3812 29.7036 13.2445 28.8096 12.2178C27.7983 11.0563 26.7872 9.90075 26.751 7.76367C26.7182 5.84853 27.4186 4.07746 28.7285 2.78223C30.0238 1.50224 31.8135 0.784582 33.7617 0.751953C33.8076 0.750704 33.8527 0.75 33.8936 0.75Z"
        stroke={color}
        strokeWidth="0.5"
      />
    </svg>
  );
}

interface AdvancedPinContentProps {
  venue: Venue;
  isSelected: boolean;
  isHovered: boolean;
  isMapperView: boolean;
  /** Always show the venue name label (Sociability mode, un-clustered pins). */
  showLabel?: boolean;
  /** Never render the inline name (names handled by separate collision markers). */
  suppressLabel?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * HTML content for an Advanced Marker pin. Public pins are composed at
 * runtime (colored frame + business-type glyph + "S" badge); mapper-view
 * pins use solid SVGs instead. The name label sits to the right of the pin,
 * Google POI style.
 */
export function AdvancedPinContent({
  venue,
  isSelected,
  isHovered,
  isMapperView,
  showLabel = false,
  suppressLabel = false,
  onMouseEnter,
  onMouseLeave,
}: AdvancedPinContentProps) {
  const isActive = isSelected || isHovered;
  const size = isSelected ? SIZE_SELECTED : isActive ? SIZE_ACTIVE : SIZE_DEFAULT;
  const height = size * PIN_ASPECT;

  const isGrey = venue.venuePinType === VenuePinType.Grey;
  const color = isGrey ? GREY : ORANGE;
  const glyphSize = size * 0.4;
  const category = venue.calcVenueType || venue.businessType || "";
  const centreTop = getPinCentreTop(size);

  return (
    <div
      role="img"
      aria-label={venue.name || "Map pin"}
      className="adv-pin-content relative flex items-end justify-center cursor-pointer"
      style={{ width: size, height }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isMapperView ? (
        <img
          src={getMapperPinSvg(venue.venuePinType)}
          width={size}
          height={size}
          alt={venue.name || ""}
          draggable={false}
          style={{ display: "block" }}
        />
      ) : (
        <>
          <SociabilityPin size={size} color={color} />

          {/* Glyph centered in the pin's white circle. */}
          <span
            className="absolute pointer-events-none"
            style={{
              left: "50%",
              top: centreTop,
              transform: "translate(-50%, -50%)",
            }}
          >
            <BusinessTypeGlyph
              businessType={venue.businessType}
              size={glyphSize}
              color={color}
            />
          </span>
        </>
      )}

      {/* Name + category, to the right of the pin (Google POI style). */}
      {venue.name && !suppressLabel && (
        <div
          className={cn(
            "absolute left-full ml-1.5 flex flex-col",
            "transition-opacity duration-150 pointer-events-none select-none",
            isSelected || isHovered || showLabel ? "opacity-100" : "opacity-0",
          )}
          style={{ top: centreTop, transform: "translateY(-55%)" }}
        >
          <span
            className="text-[13px] font-medium leading-tight whitespace-nowrap max-w-[160px] truncate"
            style={{ textShadow: NAME_HALO, color: NAME_TEXT_COLOR }}
          >
            {venue.name}
          </span>
          {category && (
            <span
              className="text-[11px] leading-tight whitespace-nowrap max-w-[160px] truncate"
              style={{ textShadow: NAME_HALO, color: CATEGORY_TEXT_COLOR }}
            >
              {category}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
